import { z } from 'zod';

import {
  calendarConnectionListResponseSchema,
  calendarConnectionResponseSchema,
  calendarSyncConflictListResponseSchema,
  calendarSyncConflictResponseSchema,
  providerBaaRecordListResponseSchema,
  providerBaaRecordResponseSchema,
  startCalendarConnectionResponseSchema,
  type CalendarConnection,
  type CalendarProvider,
  type CalendarSyncConflict,
  type ProviderBaaRecord,
  type UpdateCalendarConnectionRequest,
} from '@emr/shared';

import { apiRequest } from './client.ts';
import { fileToBase64 } from '../lib/fileToBase64.ts';

/**
 * Google Calendar / Outlook Calendar two-way sync (`BACKLOG.md` K7,
 * `docs/decisions/calendar-sync-scope.md`) — validated against `@emr/shared`'s own response schemas,
 * matching every other file in this directory's own reasoning for why.
 */

/** `POST /api/calendar-sync/:provider/start` — self-service. Returns the vendor's own consent-
 * screen URL; the caller navigates the browser there directly (`window.location.href = ...`), it is
 * never fetched. */
export async function startCalendarConnection(provider: CalendarProvider): Promise<string> {
  const response = await apiRequest(
    `/api/calendar-sync/${provider}/start`,
    startCalendarConnectionResponseSchema,
    { method: 'POST', body: {} },
  );
  return response.authorizationUrl;
}

/** `GET /api/calendar-sync/connections` — self-service, the caller's own connections only. */
export async function fetchOwnCalendarConnections(
  signal?: AbortSignal,
): Promise<readonly CalendarConnection[]> {
  const response = await apiRequest(
    '/api/calendar-sync/connections',
    calendarConnectionListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.connections;
}

/** `PATCH /api/calendar-sync/connections/:connectionId` — self-service toggle. A `baa_not_current`
 * `ApiError` (409) is the caller's to interpret when requesting `phiInclusionEnabled: true` with no
 * current BAA on file, matching `api/orgBranding.ts`'s own "not this function's job" precedent for a
 * status-coded refusal. */
export async function updateCalendarConnection(
  connectionId: string,
  input: UpdateCalendarConnectionRequest,
): Promise<CalendarConnection> {
  const response = await apiRequest(
    `/api/calendar-sync/connections/${connectionId}`,
    calendarConnectionResponseSchema,
    { method: 'PATCH', body: input },
  );
  return response.connection;
}

const noContentSchema = z.undefined();

/** `DELETE /api/calendar-sync/connections/:connectionId` — self-service disconnect. */
export async function disconnectCalendarConnection(connectionId: string): Promise<void> {
  await apiRequest(`/api/calendar-sync/connections/${connectionId}`, noContentSchema, {
    method: 'DELETE',
  });
}

/** `GET /api/calendar-sync/baa` — `org:manage_settings`. */
export async function fetchProviderBaaRecords(
  signal?: AbortSignal,
): Promise<readonly ProviderBaaRecord[]> {
  const response = await apiRequest(
    '/api/calendar-sync/baa',
    providerBaaRecordListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.records;
}

/** `POST /api/calendar-sync/baa/:providerId/:provider` — `org:manage_settings`. `application/pdf`
 * only, verified against the real bytes server-side regardless of what this function declares. */
export async function uploadProviderBaaRecord(
  providerId: string,
  provider: CalendarProvider,
  file: File,
  currentUntil: string,
): Promise<ProviderBaaRecord> {
  const contentBase64 = await fileToBase64(file);
  const response = await apiRequest(
    `/api/calendar-sync/baa/${providerId}/${provider}`,
    providerBaaRecordResponseSchema,
    {
      method: 'POST',
      body: {
        filename: file.name,
        declaredMimeType: 'application/pdf',
        contentBase64,
        currentUntil,
      },
    },
  );
  return response.record;
}

/** `GET /api/calendar-sync/conflicts` — `schedule:manage`. `providerId` narrows to one provider's
 * own open conflicts; omitted, every open conflict in the org. */
export async function fetchCalendarSyncConflicts(
  providerId?: string,
  signal?: AbortSignal,
): Promise<readonly CalendarSyncConflict[]> {
  const query = providerId === undefined ? '' : `?providerId=${encodeURIComponent(providerId)}`;
  const response = await apiRequest(
    `/api/calendar-sync/conflicts${query}`,
    calendarSyncConflictListResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.conflicts;
}

/** `POST /api/calendar-sync/conflicts/:id/resolve` — `schedule:manage`. */
export async function resolveCalendarSyncConflict(
  conflictId: string,
): Promise<CalendarSyncConflict> {
  const response = await apiRequest(
    `/api/calendar-sync/conflicts/${conflictId}/resolve`,
    calendarSyncConflictResponseSchema,
    { method: 'POST', body: {} },
  );
  return response.conflict;
}
